import {useQueryClient} from '@tanstack/react-query';
import {CACHE_TTL} from '@/config/cacheTtl';
import {
  bookingDecisionsQueryKey,
  fetchBookingDecisionsRaw,
  saveBookingDecisionRaw,
} from '@/shared/lib/bookingDecisions';

/**
 * Single shared entry point for `GET/POST /booking-decisions` (OD-1102
 * follow-up). Action Center, the Scheduler grid, and the PM booking page all
 * need to know which (participant, session type) pairs a PM has blocked —
 * without this, each was independently fetching the same per-project list on
 * its own mount with no shared cache and no way to see another surface's
 * write. Booking decisions change via PM action (not reference data), so this
 * uses the INTERACTIVE tier, in-memory only — no localStorage persistence,
 * matching Action Center's other per-project metrics.
 */
export function useBookingDecisionsFetcher() {
  const queryClient = useQueryClient();

  const fetchBookingDecisions = (projectId: string, options?: {refresh?: boolean}) =>
    queryClient.fetchQuery({
      queryKey: bookingDecisionsQueryKey(projectId),
      queryFn: () => fetchBookingDecisionsRaw(projectId),
      staleTime: options?.refresh ? 0 : CACHE_TTL.INTERACTIVE,
    });

  const invalidateBookingDecisions = (projectId: string) =>
    queryClient.invalidateQueries({queryKey: bookingDecisionsQueryKey(projectId)});

  return {
    fetchBookingDecisions,
    saveBookingDecision: saveBookingDecisionRaw,
    invalidateBookingDecisions,
  };
}
